import { useState, useMemo } from 'react';
import { CheckCircle2, GraduationCap, Eye, AlertTriangle, X, Calendar, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { useQueryClient } from '@tanstack/react-query';
import { ROUTES } from '../../api/routes';
import './CatalogoBNCC.css';

const SERIES_POR_NIVEL = [
  { nivel: 'Educa\u00e7\u00e3o Infantil', series: ['Maternal', 'Jardim I', 'Jardim II'] },
  { nivel: 'Fund. I (1\u00ba ao 5\u00ba Ano)', series: ['1\u00ba Ano', '2\u00ba Ano', '3\u00ba Ano', '4\u00ba Ano', '5\u00ba Ano'] },
  { nivel: 'Fund. II (6\u00ba ao 9\u00ba Ano)', series: ['6\u00ba Ano', '7\u00ba Ano', '8\u00ba Ano', '9\u00ba Ano'] },
  { nivel: 'Ensino M\u00e9dio', series: ['1\u00aa S\u00e9rie', '2\u00aa S\u00e9rie', '3\u00aa S\u00e9rie'] },
];

type NomePadrao = 'letra' | 'numero';

function gerarSufixo(p: NomePadrao, i: number) {
  return p === 'letra' ? String.fromCharCode(65 + i) : String(i + 1).padStart(2, '0');
}

function gerarNome(serie: string, p: NomePadrao, i: number, qtd: number, incSerie: boolean) {
  const s = gerarSufixo(p, i);
  if (qtd === 1 && incSerie) return serie;
  if (qtd === 1 && !incSerie) return `Turma ${s}`;
  if (incSerie) return `${serie} ${s}`;
  return `Turma ${s}`;
}

interface Props {
  open: boolean;
  onClose: () => void;
  turnos: { id: string; nome: string }[];
  turmasAtivas: { nome: string; serie: string; turnoOperacionalId?: string }[];
  turmasInativas: { nome: string; serie: string; turnoOperacionalId?: string }[];
}

type StatusTurma = 'nova' | 'inativa' | 'ativa';

export function BatchTurmasModal({ open, onClose, turnos, turmasAtivas, turmasInativas }: Props) {
  const escolaId = useAuthStore((s) => s.escolaId);
  const qc = useQueryClient();
  const anoAtual = new Date().getFullYear();
  const isInsideWizard = useMemo(() => {
    return !!document.getElementById('sw-actual-page-container');
  }, [open]);

  const [turnoId, setTurnoId] = useState(turnos[0]?.id || '');
  const [anoLetivo, setAnoLetivo] = useState(anoAtual);
  const [qtds, setQtds] = useState<Record<string, number>>({
    'Educação Infantil': 4,
    'Fund. I (1º ao 5º Ano)': 4,
    'Fund. II (6º ao 9º Ano)': 4,
    'Ensino Médio': 4,
  });
  const getQtdForSerie = (serie: string) => {
    const nv = SERIES_POR_NIVEL.find((n) => n.series.includes(serie));
    return nv ? (qtds[nv.nivel] ?? 4) : 4;
  };
  const [pad, setPad] = useState<NomePadrao>('letra');
  const [incSerie, setIncSerie] = useState(true);
  const [sels, setSels] = useState<Set<string>>(new Set());
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [salvando, setSalvando] = useState(false);
  const [previewList, setPreviewList] = useState<{ nome: string; serie: string; status: StatusTurma; sala: string; capacidade: number; conflito?: boolean }[]>([]);


  // Reset limpo ao abrir
  const resetState = () => {
    setTurnoId(turnos[0]?.id || '');
    setAnoLetivo(anoAtual);
    setQtds({
      'Educação Infantil': 4,
      'Fund. I (1º ao 5º Ano)': 4,
      'Fund. II (6º ao 9º Ano)': 4,
      'Ensino Médio': 4,
    });
    setPad('letra'); setIncSerie(true);
    setSels(new Set()); setStep(1); setPreviewList([]);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Mapas de lookup por (nome|serie) filtrados pelo turno selecionado
  // REGRA: a constraint do banco é (escola, nome, serie, ano_letivo) — turno NÃO faz parte.
  // Mas do ponto de vista operacional, turmas com mesmo nome+série em turnos DIFERENTES
  // são entidades distintas. O conflito só existe dentro do mesmo turno.
  const chave = (nome: string, serie: string) => `${nome.toLowerCase()}|${serie.toLowerCase()}`;
  const ativasSet = useMemo(() =>
    new Set(
      turmasAtivas
        .filter(t => !t.turnoOperacionalId || t.turnoOperacionalId === turnoId)
        .map(t => chave(t.nome, t.serie))
    ), [turmasAtivas, turnoId]);
  const inativasSet = useMemo(() =>
    new Set(
      turmasInativas
        .filter(t => !t.turnoOperacionalId || t.turnoOperacionalId === turnoId)
        .map(t => chave(t.nome, t.serie))
    ), [turmasInativas, turnoId]);

  const getStatus = (nome: string, serie: string): StatusTurma => {
    const k = chave(nome, serie);
    if (ativasSet.has(k)) return 'ativa';
    if (inativasSet.has(k)) return 'inativa';
    return 'nova';
  };

  const toggle = (s: string) => { setSels((p) => { const n = new Set(p); n.has(s) ? n.delete(s) : n.add(s); return n; }); };
  const toggleNivel = (ss: string[]) => {
    setSels((p) => { const n = new Set(p); const all = ss.every((s) => n.has(s)); ss.forEach((s) => all ? n.delete(s) : n.add(s)); return n; });
  };

  const buildPreview = () => {
    const t: { nome: string; serie: string; status: StatusTurma; sala: string; capacity: number }[] = [];
    const sorted = [...sels].sort();
    for (const serie of sorted) {
      const q = getQtdForSerie(serie);
      for (let i = 0; i < q; i++) {
        const nome = gerarNome(serie, pad, i, q, incSerie);
        t.push({ nome, serie, status: getStatus(nome, serie), sala: '', capacidade: 35 } as any);
      }
    }
    return t as any;
  };

  const goToPreview = () => { setPreviewList(buildPreview()); setStep(2); };
  const goBack = () => setStep(step === 3 ? 2 : 1);

  const removeTurma = (nome: string, serie: string) => {
    setPreviewList((p) => p.filter((t) => !(t.nome === nome && t.serie === serie)));
  };

  const addTurmaToSerie = (serie: string) => {
    const existingCount = previewList.filter((t) => t.serie === serie).length;
    const nome = gerarNome(serie, pad, existingCount, existingCount + 1, incSerie);
    if (previewList.some((t) => t.nome === nome && t.serie === serie)) return;
    setPreviewList((p) => [...p, { nome, serie, status: getStatus(nome, serie), sala: '', capacidade: 35 }]);
  };

  const goToStep3 = () => {
    // Auto-preencher sala com numeracao crescente
    let salaNum = 1;
    setPreviewList((p) => p.map((t) => t.conflito ? t : { ...t, sala: String(salaNum++) }));
    setStep(3);
  };

  // Editar campo de uma turma no step 3
  const updateTurma = (idx: number, field: 'nome' | 'sala' | 'capacidade', value: string | number) => {
    setPreviewList((p) => p.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  // Limpar todos os valores de uma coluna
  const clearColumn = (field: 'sala' | 'capacidade') => {
    setPreviewList((p) => p.map((t) => ({ ...t, [field]: field === 'sala' ? '' : 35 })));
  };

  // Navegacao por teclado (Enter, ArrowUp, ArrowDown) entre campos da mesma coluna
  const handleFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, col: string, rowIdx: number) => {
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = document.querySelector<HTMLInputElement>(`input[data-col="${col}"][data-row="${rowIdx + 1}"]`);
      if (next) {
        next.focus();
        next.select();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = document.querySelector<HTMLInputElement>(`input[data-col="${col}"][data-row="${rowIdx - 1}"]`);
      if (prev) {
        prev.focus();
        prev.select();
      }
    }
  };

  // Turmas que serão enviadas ao backend (novas + inativas para reativar)
  const turmasAcionar = previewList.filter((t) => t.status !== 'ativa');
  const turmasNovas = previewList.filter((t) => t.status === 'nova');
  const turmasReativar = previewList.filter((t) => t.status === 'inativa');
  const turmasIgnoradas = previewList.filter((t) => t.status === 'ativa');

  // Agrupar por nivel para exibicao hierarquica (ordem cronologica do SERIES_POR_NIVEL)
  const grouped = useMemo(() => {
    const result: { nivel: string; turmas: typeof previewList }[] = [];
    for (const nv of SERIES_POR_NIVEL) {
      const turmasDoNivel = previewList.filter((t) => nv.series.includes(t.serie));
      if (turmasDoNivel.length > 0) result.push({ nivel: nv.nivel, turmas: turmasDoNivel });
    }
    return result;
  }, [previewList]);

  const handleSalvar = async () => {
    if (turmasAcionar.length === 0) { toast.warning('Nenhuma turma para criar ou reativar'); return; }
    if (!turnoId || !escolaId) { toast.error('Dados incompletos'); return; }
    setSalvando(true);
    try {
      const { data } = await api.post(ROUTES.turmas.batch(escolaId), {
        turmas: turmasAcionar.map((t) => ({ nome: t.nome, serie: t.serie, turnoOperacionalId: turnoId, anoLetivo, sala: t.sala || undefined, capacidade: t.capacidade || 35 })),
      });

      const criadas: number = data.criadas ?? 0;
      const reativadas: number = data.reativadas ?? 0;
      const falhas: string[] = data.falhas ?? [];

      const partes: string[] = [];
      if (criadas > 0) partes.push(`${criadas} criada(s)`);
      if (reativadas > 0) partes.push(`${reativadas} reativada(s)`);
      if (falhas.length > 0) partes.push(`${falhas.length} falha(s)`);

      if (falhas.length > 0) {
        toast.warning(`${partes.join(', ')}. Falhas: ${falhas.slice(0, 3).join(' | ')}${falhas.length > 3 ? '…' : ''}`, { duration: 8000 });
      } else {
        toast.success(`✅ ${partes.join(' e ')} com sucesso!`);
      }

      qc.invalidateQueries({ queryKey: ['turmas'] });
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      resetState(); onClose();
    } catch { toast.error('Erro ao processar turmas'); }
    finally { setSalvando(false); }
  };


  if (!open) return null;

  const qEx = qtds['Fund. I (1º ao 5º Ano)'] ?? 4;
  const ex = gerarNome('1\u00ba Ano', pad, 0, qEx > 1 ? 2 : 1, incSerie);
  const sty: React.CSSProperties = {
    padding: '8px 12px', borderRadius: 8, fontSize: '0.85rem',
    border: '1px solid var(--border-color, #d1d5db)', background: 'var(--bg-card, white)', cursor: 'pointer', outline: 'none',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-lab-modal="batch-turmas"
      className="modal ac-modal batch-turmas-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      style={{
        position: isInsideWizard ? 'absolute' : 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isInsideWizard ? '12px' : '24px'
      }}
    >
      <div
        className="ac-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-main, #f8fafc)',
          borderRadius: 16,
          width: isInsideWizard ? '100%' : '95vw',
          maxWidth: 780,
          maxHeight: isInsideWizard ? '100%' : '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          overflow: 'hidden'
        }}
      >

        {/* HEADER AZUL */}
        <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)', padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'white', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <GraduationCap size={20} />
            <div>
              <h2 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 800 }}>Assistente de Criação de Turmas</h2>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Fechar"
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}
          >
            <X size={16} />
          </button>
        </div>

        {/* BODY */}
        <div style={{ flex: 1, overflow: 'auto', padding: isInsideWizard ? '12px 14px' : '16px 20px' }}>
          {step === 1 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Row 1 */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                <div className="sm:col-span-2">
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', marginBottom: 4, display: 'block' }}>Turno</label>
                  <select style={{ ...sty, width: '100%' }} value={turnoId} onChange={(e) => setTurnoId(e.target.value)}>
                    {turnos.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', marginBottom: 4, display: 'block' }}>Ano Letivo</label>
                  <select style={{ ...sty, width: '100%' }} value={anoLetivo} onChange={(e) => setAnoLetivo(Number(e.target.value))}>
                    {[anoAtual + 1, anoAtual, anoAtual - 1].map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 2: Padrao do nome */}
              <div className="flex flex-col gap-2 p-2.5 rounded-xl border border-slate-200 bg-white/70">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b7280' }}>Padrão do nome:</label>
                    <span style={{
                      background: 'linear-gradient(135deg, #2563eb, #3b82f6)', color: 'white',
                      padding: '2px 10px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 700,
                      letterSpacing: '0.02em',
                    }}>{ex}</span>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', cursor: 'pointer' }}>
                    <div onClick={() => setIncSerie(!incSerie)} style={{ width: 34, height: 18, borderRadius: 10, background: incSerie ? '#2563eb' : '#d1d5db', position: 'relative', cursor: 'pointer', transition: 'all 0.2s' }}>
                      <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: incSerie ? 18 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                    Incluir série no nome
                  </label>
                </div>
                <div className="flex gap-2">
                  {([{ key: 'letra' as NomePadrao, label: 'Letra (A, B, C)' }, { key: 'numero' as NomePadrao, label: 'Número (01, 02)' }]).map((p) => (
                    <button key={p.key} onClick={() => setPad(p.key)} style={{
                      flex: 1, padding: '6px 12px', borderRadius: 8, fontSize: '0.75rem', fontWeight: pad === p.key ? 700 : 500, cursor: 'pointer',
                      border: pad === p.key ? '2px solid #2563eb' : '1px solid var(--border-color, #d1d5db)',
                      background: pad === p.key ? '#dbeafe' : 'transparent', color: pad === p.key ? '#1e40af' : '#374151', transition: 'all 0.15s',
                    }}>{p.label}</button>
                  ))}
                </div>
              </div>

              {/* Series */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Selecione as séries ({sels.size} selecionadas)
                  </label>
                </div>
                <div className="flex flex-col gap-2.5">
                  {SERIES_POR_NIVEL.map((nv) => {
                    const allSel = nv.series.every((s) => sels.has(s));
                    const countSelected = nv.series.filter((s) => sels.has(s)).length;
                    const q = qtds[nv.nivel] ?? 4;
                    const totalTurmas = countSelected * q;
                    return (
                      <div
                        key={nv.nivel}
                        className="border border-[var(--border-color,#e2e8f0)] rounded-xl p-2.5 sm:p-3 bg-[var(--bg-card,white)] shadow-sm flex flex-col gap-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className="font-bold text-xs sm:text-sm text-[var(--text-main,#1e293b)] truncate">{nv.nivel}</span>
                            <button
                              type="button"
                              onClick={() => toggleNivel(nv.series)}
                              className="text-[10px] sm:text-[11px] font-semibold text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0 shrink-0"
                            >
                              {allSel ? 'Desmarcar' : 'Selecionar tudo'}
                            </button>
                          </div>
                          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                            <select
                              value={q}
                              onChange={(e) => {
                                const val = Number(e.target.value);
                                setQtds((prev) => ({ ...prev, [nv.nivel]: val }));
                              }}
                              className="text-[11px] sm:text-xs py-1 px-1.5 sm:px-2 rounded-lg border border-slate-300 bg-white text-slate-700 outline-none cursor-pointer"
                            >
                              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                                <option key={n} value={n}>
                                  {n} {n > 1 ? 'turmas' : 'turma'} por série
                                </option>
                              ))}
                            </select>
                            <span className={`text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${countSelected > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400'}`}>
                              {totalTurmas} {totalTurmas === 1 ? 'turma' : 'turmas'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Chips das séries em largura total */}
                        <div className="flex flex-wrap gap-1.5">
                          {nv.series.map((s) => {
                            const sel = sels.has(s);
                            return (
                              <button
                                key={s}
                                type="button"
                                className={`catalogo-chip ${sel ? 'selected' : ''}`}
                                onClick={() => toggle(s)}
                              >
                                {sel && <CheckCircle2 size={12} />}
                                {s}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : step === 2 ? (
            /* STEP 2: Preview hierarquico */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Resumo de ações */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {turmasNovas.length > 0 && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '4px 10px', fontSize: '0.74rem', fontWeight: 700, color: '#166534' }}>
                    <CheckCircle2 size={12} /> {turmasNovas.length} a criar
                  </span>
                )}
                {turmasReativar.length > 0 && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '4px 10px', fontSize: '0.74rem', fontWeight: 700, color: '#1e40af' }}>
                    <AlertTriangle size={12} /> {turmasReativar.length} a reativar (estavam inativas)
                  </span>
                )}
                {turmasIgnoradas.length > 0 && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '4px 10px', fontSize: '0.74rem', fontWeight: 600, color: '#94a3b8' }}>
                    <X size={12} /> {turmasIgnoradas.length} já ativas (ignoradas)
                  </span>
                )}
                <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#6b7280', alignSelf: 'center' }}>{turnos.find(t => t.id === turnoId)?.nome} | {anoLetivo}</span>
              </div>

              {/* Hierarquia agrupada */}
              {grouped.map(({ nivel, turmas }) => (
                <div key={nivel}>
                  <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#1e293b', marginBottom: 6, padding: '4px 0', borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
                    {nivel}
                    <span style={{ fontWeight: 400, fontSize: '0.68rem', color: '#9ca3af', marginLeft: 6 }}>({turmas.filter(t => t.status !== 'ativa').length} a processar)</span>
                  </div>
                  {(() => {
                    const bySerie = new Map<string, typeof turmas>();
                    // Manter ordem do SERIES_POR_NIVEL
                    const nivelDef = SERIES_POR_NIVEL.find((n) => n.nivel === nivel);
                    const serieOrder = nivelDef ? nivelDef.series : [];
                    turmas.forEach((t) => { if (!bySerie.has(t.serie)) bySerie.set(t.serie, []); bySerie.get(t.serie)!.push(t); });
                    const sortedEntries = [...bySerie.entries()].sort((a, b) => serieOrder.indexOf(a[0]) - serieOrder.indexOf(b[0]));
                    return sortedEntries.map(([serie, items]) => (
                      <div key={serie} style={{ marginBottom: 10, paddingLeft: 12 }}>
                        <div style={{ marginBottom: 5 }}>
                          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>{serie}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                          {items.map((t) => {
                            const isNova = t.status === 'nova';
                            const isInativa = t.status === 'inativa';
                            const isAtiva = t.status === 'ativa';
                            const bg = isNova ? '#f0fdf4' : isInativa ? '#eff6ff' : '#f8fafc';
                            const border = isNova ? '#bbf7d0' : isInativa ? '#bfdbfe' : '#e2e8f0';
                            const color = isNova ? '#166534' : isInativa ? '#1e40af' : '#94a3b8';
                            const label = isInativa ? ' (reativar)' : isAtiva ? ' (já ativa)' : '';
                            return (
                              <div key={`${t.nome}-${t.serie}`} style={{
                                display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6,
                                background: bg, border: `1px solid ${border}`,
                                fontSize: '0.78rem', fontWeight: 600, color,
                                opacity: isAtiva ? 0.5 : 1,
                              }}>
                                {isNova && <CheckCircle2 size={11} />}
                                {isInativa && <AlertTriangle size={11} />}
                                {isAtiva && <X size={11} />}
                                {t.nome}
                                {label && <span style={{ fontSize: '0.65rem', fontWeight: 500, opacity: 0.8 }}>{label}</span>}
                                {!isAtiva && (
                                  <button onClick={() => removeTurma(t.nome, t.serie)} title="Remover" style={{
                                    background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginLeft: 2, color: '#ef4444', display: 'flex',
                                  }}><Trash2 size={11} /></button>
                                )}
                              </div>
                            );
                          })}
                          {/* Botao + Turma inline apos ultima turma */}
                          <button onClick={() => addTurmaToSerie(serie)} style={{
                            background: 'none', border: '1px dashed #93c5fd', borderRadius: 6,
                            padding: '5px 10px', cursor: 'pointer', fontSize: '0.72rem', color: '#2563eb',
                            display: 'flex', alignItems: 'center', gap: 3, fontWeight: 600,
                          }}><Plus size={11} /> Turma</button>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              ))}

              {previewList.length === 0 && (
                <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af', fontSize: '0.85rem' }}>Nenhuma turma na lista</div>
              )}
            </div>
          ) : (
            /* STEP 3: Personalizar sala/capacidade/nome — agrupado hierarquicamente */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                  {turmasNovas.length} turma(s) a criar
                  <span style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 400, marginLeft: 8 }}>{turnos.find(t => t.id === turnoId)?.nome} | {anoLetivo}</span>
                </span>
              </div>

              {/* Header da tabela com botoes limpar */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 70px', padding: '6px 12px 2px 24px', fontSize: '0.65rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <span>Nome da Turma</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span>Sala</span>
                  <button onClick={() => clearColumn('sala')} type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.58rem', color: '#ef4444', padding: 0, textAlign: 'left', fontWeight: 600 }}>Limpar</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span>Capacidade</span>
                  <button onClick={() => clearColumn('capacidade')} type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.58rem', color: '#ef4444', padding: 0, textAlign: 'left', fontWeight: 600 }}>Limpar</button>
                </div>
              </div>

              {/* Agrupado por nivel e serie */}
              {(() => {
                let rowCounter = 0;
                return grouped.map(({ nivel, turmas: turmasNivel }) => {
                  const turmasNovasNivel = turmasNivel.filter((t) => t.status !== 'ativa');
                  if (turmasNovasNivel.length === 0) return null;
                  const bySerie = new Map<string, typeof turmasNivel>();
                  const nivelDef = SERIES_POR_NIVEL.find((n) => n.nivel === nivel);
                  const serieOrder = nivelDef ? nivelDef.series : [];
                  turmasNovasNivel.forEach((t) => { if (!bySerie.has(t.serie)) bySerie.set(t.serie, []); bySerie.get(t.serie)!.push(t); });
                  const sortedEntries = [...bySerie.entries()].sort((a, b) => serieOrder.indexOf(a[0]) - serieOrder.indexOf(b[0]));

                  return (
                    <div key={nivel}>
                      <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#1e293b', marginBottom: 4, padding: '4px 0', borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
                        {nivel}
                        <span style={{ fontWeight: 400, fontSize: '0.68rem', color: '#9ca3af', marginLeft: 6 }}>({turmasNovasNivel.length} turmas)</span>
                      </div>
                      {sortedEntries.map(([serie, items]) => (
                        <div key={serie} style={{ marginBottom: 8, paddingLeft: 8 }}>
                          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', marginBottom: 4, paddingTop: 4 }}>{serie}</div>
                          {items.map((t) => {
                            const realIdx = previewList.indexOf(t);
                            const currentRow = rowCounter++;
                            return (
                              <div key={`${t.nome}-${t.serie}`} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 70px', padding: '4px 12px 4px 16px', alignItems: 'center', borderBottom: '1px solid var(--border-color, #f3f4f6)' }}>
                                <input data-col="nome" data-row={currentRow} value={t.nome} onChange={(e) => updateTurma(realIdx, 'nome', e.target.value)} onKeyDown={(e) => handleFieldKeyDown(e, 'nome', currentRow)} onFocus={(e) => e.target.select()} style={{ border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 6, padding: '5px 8px', fontSize: '0.8rem', background: 'var(--bg-card, white)', color: 'var(--text-main, #1e293b)', outline: 'none', width: '92%', fontWeight: 600 }} />
                                <input data-col="sala" data-row={currentRow} value={t.sala} onChange={(e) => updateTurma(realIdx, 'sala', e.target.value)} onKeyDown={(e) => handleFieldKeyDown(e, 'sala', currentRow)} onFocus={(e) => e.target.select()} placeholder="Ex: 101" style={{ border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 6, padding: '5px 8px', fontSize: '0.78rem', background: 'var(--bg-card, white)', color: 'var(--text-main, #1e293b)', outline: 'none', width: '85%' }} />
                                <input
                                  data-col="cap"
                                  data-row={currentRow}
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  maxLength={3}
                                  value={t.capacidade || ''}
                                  onChange={(e) => updateTurma(realIdx, 'capacidade', parseInt(e.target.value.replace(/\D/g, '')) || 0)}
                                  onKeyDown={(e) => handleFieldKeyDown(e, 'cap', currentRow)}
                                  onFocus={(e) => e.target.select()}
                                  placeholder="35"
                                  style={{ border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 6, padding: '5px 8px', fontSize: '0.78rem', background: 'var(--bg-card, white)', color: 'var(--text-main, #1e293b)', outline: 'none', width: '60px', textAlign: 'center' }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ padding: '12px 24px', display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, white)', flexShrink: 0 }}>
          {step === 1 ? (
            <>
              <button
                type="button"
                onClick={handleClose}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-color, #d1d5db)', background: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary, #475569)' }}
              >
                Cancelar
              </button>
              <button
                data-testid="btn-visualizar-turmas-batch"
                className="btn-visualizar-turmas-batch"
                onClick={goToPreview}
                disabled={sels.size === 0}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: 'none', cursor: sels.size === 0 ? 'not-allowed' : 'pointer',
                  background: sels.size === 0 ? '#d1d5db' : 'linear-gradient(135deg, #2563eb, #3b82f6)', color: 'white',
                  fontSize: '0.82rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6,
                  boxShadow: sels.size > 0 ? '0 2px 8px rgba(37,99,235,0.3)' : 'none',
                }}
              >
                <Eye size={16} /> Visualizar ({buildPreview().length} turmas)
              </button>
            </>
          ) : step === 2 ? (
            <>
              <button onClick={goBack} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-color, #d1d5db)', background: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>Voltar</button>
              <button
                data-testid="btn-configurar-turmas-batch"
                className="btn-configurar-turmas-batch"
                onClick={turmasAcionar.length === 0 ? () => { toast.info('Todas as turmas selecionadas já estão ativas na escola.'); handleClose(); } : goToStep3}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: 'none',
                  cursor: 'pointer',
                  background: turmasAcionar.length === 0 ? 'linear-gradient(135deg, #059669, #10b981)' : 'linear-gradient(135deg, #2563eb, #3b82f6)',
                  color: 'white', fontSize: '0.82rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6,
                  boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
                }}
              >
                {turmasAcionar.length === 0 ? (
                  <>
                    <CheckCircle2 size={16} /> Concluir (Turmas já ativas)
                  </>
                ) : (
                  'Configurar Sala e Capacidade'
                )}
              </button>
            </>
          ) : (
            <>
              <button onClick={goBack} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-color, #d1d5db)', background: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>Voltar</button>
              <button
                data-testid="btn-salvar-turmas-batch"
                className="btn-salvar-turmas-batch"
                onClick={turmasNovas.length === 0 && turmasReativar.length === 0 ? () => { toast.info('Turmas já cadastradas.'); handleClose(); } : handleSalvar}
                disabled={salvando}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: 'none',
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #059669, #10b981)',
                  color: 'white', fontSize: '0.82rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6,
                  boxShadow: '0 2px 8px rgba(5,150,105,0.3)',
                }}
              >
                <CheckCircle2 size={16} /> {salvando ? 'Criando...' : turmasNovas.length === 0 && turmasReativar.length === 0 ? 'Concluir' : `Criar ${turmasNovas.length} Turma(s)`}
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
